import {
    groupLifeService,
    type GroupLifeAsyncImportStatusResponse,
} from '@/services';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

export type AsyncImportProgress = {
  current: number;
  total: number;
  percent: number; // 0-100
};

/**
 * Polling do status de importação assíncrona via React Query. Atualiza a cada
 * 3s enquanto o status não for terminal; para automaticamente quando
 * `COMPLETED`, `UPDATE` ou `FAILED`. Também aplica timeout de segurança de 12 minutos
 * SEM PROGRESSO — cinto de segurança visual: o backend tem timeout próprio de
 * 10 min que marca FAILED e libera os CPFs antes de o front chegar aqui.
 *
 * Uso típico:
 * ```ts
 * const { status, progress, errors, members, isError, timedOut } =
 *   useAsyncMembersImport({ policyId, importId, enabled: !!importId });
 * ```
 */
export function useAsyncMembersImport(params: {
  policyId: string | null;
  importId: string | null;
  enabled: boolean;
  isProposal?: boolean;
}) {
  const lastProgressAtRef = useRef<number | null>(null);
  const lastCreatedRef = useRef<number>(-1);
  const TIMEOUT_MS = 12 * 60 * 1000;
  const POLL_INTERVAL_MS = 3000;

  // Âncora do timeout: renovada no primeiro enable e a cada avanço de
  // `totalCreated` — importações grandes que progridem devagar não são
  // derrubadas; só a ausência total de progresso dispara o timeout.
  useEffect(() => {
    if (params.enabled && params.importId && lastProgressAtRef.current == null) {
      lastProgressAtRef.current = Date.now();
      lastCreatedRef.current = -1;
    }
    if (!params.enabled || !params.importId) {
      lastProgressAtRef.current = null;
      lastCreatedRef.current = -1;
    }
  }, [params.enabled, params.importId]);

  const query = useQuery({
    queryKey: [
      'group-life-async-import',
      params.isProposal ? 'proposal' : 'quote',
      params.policyId,
      params.importId,
    ],
    queryFn: async () => {
      if (!params.policyId || !params.importId) return null;
      const res = await groupLifeService.getAsyncImportStatus({
        policyId: params.policyId,
        importId: params.importId,
        isProposal: params.isProposal,
      });
      if (!res.success || !res.data) {
        const error = new Error(
          res.error ?? 'Falha ao consultar status da importação',
        ) as Error & { status?: number };
        error.status = res.status;
        throw error;
      }
      return res.data;
    },
    enabled: params.enabled && !!params.policyId && !!params.importId,
    refetchInterval: query => {
      if (query.state.status === 'error') return false;
      const status = query.state.data?.status;
      if (
        status === 'COMPLETED' ||
        status === 'UPDATE' ||
        status === 'FAILED'
      ) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });

  // Renova a âncora quando o backend reporta avanço no totalCreated.
  const reportedCreated = query.data?.totalCreated ?? 0;
  if (reportedCreated > lastCreatedRef.current) {
    lastCreatedRef.current = reportedCreated;
    if (lastProgressAtRef.current != null) {
      lastProgressAtRef.current = Date.now();
    }
  }

  // Timeout cobre QUALQUER estado não-terminal (PARSING e HYDRATING) — antes
  // exigia `status === 'PARSING'`, e um job preso em HYDRATING pollava para
  // sempre sem nem o timeout do front salvar.
  const currentStatus = query.data?.status;
  const timedOut =
    lastProgressAtRef.current != null &&
    Date.now() - lastProgressAtRef.current > TIMEOUT_MS &&
    currentStatus !== 'COMPLETED' &&
    currentStatus !== 'UPDATE' &&
    currentStatus !== 'FAILED';

  const status: GroupLifeAsyncImportStatusResponse['status'] =
    timedOut || query.isError
      ? 'FAILED'
      : query.data?.status === 'UPDATE'
        ? 'COMPLETED'
        : (query.data?.status ?? 'PARSING');

  const progress: AsyncImportProgress = useMemo(() => {
    const total = query.data?.totalExpected ?? 0;
    const current = query.data?.totalCreated ?? 0;
    const percent = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
    return { current, total, percent };
  }, [query.data]);

  return {
    status,
    progress,
    errors: query.data?.errors ?? [],
    members: query.data?.members ?? [],
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    timedOut,
  };
}
